import { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Image, X } from 'lucide-react';
import type { MediaBaseResponse } from '@/types/media.types';
import { useMediaGetById } from '@brainvaulttech/dgr-react-sdk';
import Loader from '@/components/Loader';

export interface ImageType {
  id: string;
  name: string;
  image: File | null | undefined;
  mediaId?: string | null;
  mediaData?: MediaBaseResponse | null;
}
export const ImageDisplayDialog = ({
  attachmentId,
  attachments,
  onClose,
}: {
  attachmentId: string;
  attachments: ImageType[];
  onClose: () => void;
}) => {
  const [isImageError, setIsImageError] = useState<boolean>(false);
  const [currIndex, setCurrIndex] = useState<number>(
    attachments?.findIndex((attachment) => attachment.id === attachmentId) ?? 0
  );
  const currentAttachment = attachments[currIndex];
  const { data, isFetching } = useMediaGetById(currentAttachment.mediaId!, {
    query: {
      queryKey: ['media', currentAttachment.mediaId],
      enabled: !!currentAttachment.mediaId && !(currentAttachment.image instanceof File),
      staleTime: 1000 * 60 * 15,
    },
  });
  const handlePrevious = () => {
    setCurrIndex(currIndex - 1);
    setIsImageError(false);
  };
  const handleNext = () => {
    setCurrIndex(currIndex + 1);
    setIsImageError(false);
  };

  const imageSrc = useMemo(() => {
    if (data?.data.url) {
      return data?.data.url;
    } else if (currentAttachment.image instanceof File) {
      return URL.createObjectURL(currentAttachment.image);
    }
    return undefined;
  }, [currentAttachment, data]);

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="flex flex-col h-[80vh] p-0 gap-0 bg-transparent max-w-[50vw] border-none shadow-none justify-center select-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:border-none focus-visible:border-none">
        <DialogHeader className="flex bg-[#F5F5F5] h-[48px] rounded-t-md !border-[0px] !focus:border-none ml-[70px] mr-[70px] select-none">
          <DialogTitle className="flex justify-between text-base t-4 px-3 py-4 !font-medium select-none">
            <span>Image {currIndex + 1}</span>
            <X className="h-4 w-4 cursor-pointer" onClick={onClose} />
          </DialogTitle>
          <DialogDescription className="sr-only">Image preview</DialogDescription>
        </DialogHeader>
        <div className="flex p-0 m-0 w-[100%] rounded border-none focus:border-none h-[100%] max-h-[60vh] relative pl-[70px] pr-[70px] overflow-hidden">
          {currIndex > 0 && (
            <div className="absolute w-8 h-8 items-center left-0 top-1/2 transform -translate-y-1/2 bg-gray-200 p-2 rounded z-10 cursor-pointer">
              <ChevronLeft className="h-4 w-4" onClick={handlePrevious} />
            </div>
          )}
          <div className="bg-white w-full rounded-b-md">
            <Loader isLoading={isFetching}>
              {imageSrc && !isImageError ? (
                <img
                  src={imageSrc}
                  alt="image"
                  className="w-full h-full object-contain"
                  onError={() => {
                    setIsImageError(true);
                  }}
                />
              ) : (
                <div className="flex flex-row gap-4 h-full items-center pb-[48px] justify-center align-middle">
                  <Image className="w-12 h-12 text-primary" strokeWidth={1} />
                  <span className="text-primary">Unable to load image.</span>
                </div>
              )}
            </Loader>
          </div>

          {currIndex < attachments.length - 1 && (
            <div className="absolute h-8 w-8 right-0 top-1/2 transform -translate-y-1/2 bg-gray-200 p-2 rounded z-10 cursor-pointer">
              <ChevronRight className="h-4 w-4" onClick={handleNext} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
